import { createFileRoute, Link } from "@tanstack/react-router";
import { requireAuth } from "@/lib/route-guards";
import { LoadingProvider } from "@/contexts/loading-context";
import { useEffect, useState } from "react";
import { useDuplicateValues } from "@/hooks/use-kura-queries";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Pagination } from "@/components/ui/pagination";
import {
    ArrowLeft,
} from "lucide-react";
import { Input } from "@/components/ui/input";

export const Route = createFileRoute(
    "/_authenticated/kura/$kuraId/duplicates"
)({
    beforeLoad: requireAuth,
    component: () => (
        <LoadingProvider>
            <KuraDuplicatesPage />
        </LoadingProvider>
    ),
});

function KuraDuplicatesPage() {
    const { kuraId } = Route.useParams();
    const [participantPageIndex, setParticipantPageIndex] = useState(0);
    const [participantPageSize, setParticipantPageSize] = useState(50);

    const [propertyPageIndex, setPropertyPageIndex] = useState(0);
    const [propertyPageSize, setPropertyPageSize] = useState(50);

    const [participantSearch, setParticipantSearch] = useState("");
    const [propertySearch, setPropertySearch] = useState("");
    // her tablo için ayrı hook
    const { data: participantData, isLoading: isLoadingParticipant } =
        useDuplicateValues(kuraId, participantPageIndex, participantPageSize, participantSearch);

    const { data: propertyData, isLoading: isLoadingProperty } =
        useDuplicateValues(kuraId, propertyPageIndex, propertyPageSize, propertySearch);

    const hasParticipantBank =
        (participantData?.participantDuplicates?.count ?? 0) > 0;

    const hasParticipantOrder =
        (participantData?.participantOrderNoDuplicates?.count ?? 0) > 0;

    const hasPropertyBank =
        (propertyData?.propertyDuplicates?.count ?? 0) > 0;

    const hasPropertyOrder =
        (propertyData?.propertyOrderNoDuplicates?.count ?? 0) > 0;


    type ActiveTab =
        | "participant-bank"
        | "participant-order"
        | "property-bank"
        | "property-order"
        | null;

    const [activeTab, setActiveTab] = useState<ActiveTab>(null);



    useEffect(() => {
        if (!activeTab) {
            if (hasParticipantBank) {
                setActiveTab("participant-bank");
            } else if (hasParticipantOrder) {
                setActiveTab("participant-order");
            } else if (hasPropertyBank) {
                setActiveTab("property-bank");
            } else if (hasPropertyOrder) {
                setActiveTab("property-order");
            }
        }
    }, [
        activeTab,
        hasParticipantBank,
        hasParticipantOrder,
        hasPropertyBank,
        hasPropertyOrder
    ]);


    return (
        <div className="p-6 space-y-8">
            {/* Geri butonu */}
            <div className="flex items-center justify-between">
                <h1 className="text-xl font-bold">Mükerrer Kayıtlar</h1>
                <Button asChild variant="outline">
                    <Link to="/kura/$kuraId/edit" params={{ kuraId }}>
                        <ArrowLeft className="h-4 w-4 mr-2" />
                        Düzenleme Sayfasına Dön
                    </Link>
                </Button>
            </div>

            {(isLoadingParticipant || isLoadingProperty) ? (
                <p>Yükleniyor...</p>
            ) : (
                hasParticipantBank ||
                hasParticipantOrder ||
                hasPropertyBank ||
                hasPropertyOrder
            ) ? (
                <>
                    {/* Tab butonları */}
                    <div className="flex gap-2 border-b pb-2">
                        {hasParticipantBank && (
                            <Button
                                variant={activeTab === "participant-bank" ? "default" : "outline"}
                                onClick={() => setActiveTab("participant-bank")}
                            >
                                Katılımcı – Banka Başvuru No
                            </Button>
                        )}

                        {hasParticipantOrder && (
                            <Button
                                variant={activeTab === "participant-order" ? "default" : "outline"}
                                onClick={() => setActiveTab("participant-order")}
                            >
                                Katılımcı – Sıra No
                            </Button>
                        )}

                        {hasPropertyBank && (
                            <Button
                                variant={activeTab === "property-bank" ? "default" : "outline"}
                                onClick={() => setActiveTab("property-bank")}
                            >
                                Konut – Banka Başvuru No
                            </Button>
                        )}

                        {hasPropertyOrder && (
                            <Button
                                variant={activeTab === "property-order" ? "default" : "outline"}
                                onClick={() => setActiveTab("property-order")}
                            >
                                Konut – Sıra No
                            </Button>
                        )}
                    </div>


                    {/* İçerik */}
                    <div>
                        {activeTab === "participant-bank" && (
                            <DuplicateTable
                                data={participantData?.participantDuplicates}
                                pageIndex={participantPageIndex}
                                setPageIndex={setParticipantPageIndex}
                                pageSize={participantPageSize}
                                setPageSize={setParticipantPageSize}
                                search={participantSearch}
                                setSearch={setParticipantSearch}
                                title="Banka Başvuru No"
                            />
                        )}

                        {activeTab === "participant-order" && (
                            <DuplicateTable
                                data={participantData?.participantOrderNoDuplicates}
                                pageIndex={participantPageIndex}
                                setPageIndex={setParticipantPageIndex}
                                pageSize={participantPageSize}
                                setPageSize={setParticipantPageSize}
                                search={participantSearch}
                                setSearch={setParticipantSearch}
                                title="Sıra No"
                            />
                        )}

                        {activeTab === "property-bank" && (
                            <DuplicateTable
                                data={propertyData?.propertyDuplicates}
                                pageIndex={propertyPageIndex}
                                setPageIndex={setPropertyPageIndex}
                                pageSize={propertyPageSize}
                                setPageSize={setPropertyPageSize}
                                search={propertySearch}
                                setSearch={setPropertySearch}
                                title="Banka Başvuru No"
                            />
                        )}

                        {activeTab === "property-order" && (
                            <DuplicateTable
                                data={propertyData?.propertyOrderNoDuplicates}
                                pageIndex={propertyPageIndex}
                                setPageIndex={setPropertyPageIndex}
                                pageSize={propertyPageSize}
                                setPageSize={setPropertyPageSize}
                                search={propertySearch}
                                setSearch={setPropertySearch}
                                title="Sıra No"
                            />
                        )}
                    </div>
                </>
            ) : (
                <p>Hiç mükerrer kayıt bulunamadı.</p>
            )}
        </div>
    );
}


function DuplicateTable({
    data,
    pageIndex,
    setPageIndex,
    pageSize,
    setPageSize,
    search,
    setSearch,
    title
}: {
    data: { items: any[]; count: number } | undefined;
    pageIndex: number;
    setPageIndex: React.Dispatch<React.SetStateAction<number>>;
    pageSize: number;
    setPageSize: React.Dispatch<React.SetStateAction<number>>;
    search: string;
    setSearch: React.Dispatch<React.SetStateAction<string>>;
    title: string;
}) {
    const [searchTerm, setSearchTerm] = useState(search);

    if (!data) return null;
    const pageCount = Math.ceil(data.count / pageSize);

    return (
        <>
            {/* Arama kutusu */}
            <div className="flex items-center gap-2 mb-4">
                <Input
                    placeholder={`${title} ara...`}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="max-w-xs"
                />
                <Button
                    variant="default"
                    onClick={() => {
                        setSearch(searchTerm);
                        setPageIndex(0);
                    }}
                >
                    Ara
                </Button>
            </div>
            <Table className="table-fixed">
                <TableHeader>
                    <TableRow>
                        <TableHead className="w-[320px] py-4">
                            {title}
                        </TableHead>
                    </TableRow>
                </TableHeader>

                <TableBody>
                    {data.items.map((item: any, idx: number) => (
                        <TableRow key={idx} className="h-6">
                            <TableCell className="py-1.5 text-sm">

                                {item.bankApplicationNo ?? item.orderNo}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>


            {/* Pagination */}
            <Pagination
                pageIndex={pageIndex}
                setPageIndex={setPageIndex}
                pageSize={pageSize}
                setPageSize={setPageSize}
                totalPages={pageCount}
                zeroBased={true}
            />
        </>
    );
}
